import type { AgentConfig, Stage } from '../schema'

export interface CreateAgentDto {
  name: string
  systemPrompt: string
  initialMessage: string
  tone?: string
  model?: string
  language?: string
  timezone?: string
  responseDelay?: number
  stages?: Stage[]
  companyProfile?: {
    name?: string
    sector?: string
    website?: string
    about?: string
  }
  knowledgeBase?: {
    files?: Array<{
      id: string
      name: string
      size: number
      url: string
    }>
    websites?: string[]
    youtubeLinks?: string[]
    qaPairs?: Array<{
      question: string
      answer: string
    }>
  }
  isActive?: boolean
  canRespondAudio?: boolean
  audioVoice?: string
  integrations?: Record<string, unknown>
}

export interface AgentEntity {
  id: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  name: string
  systemPrompt: string
  initialMessage: string
  tone: string
  model: string
  language: string
  timezone: string
  responseDelay: number
  stages: Stage[] | null
  companyProfile: any
  knowledgeBase: any
  status: string
  audioEnabled: boolean
  audioVoice: string
  integrations: any
  messageCount: number
  teamId: number
  isDefault: boolean
}

/**
 * Convert frontend AgentConfig to backend CreateAgentDto
 */
export function toCreateAgentDto(config: AgentConfig): CreateAgentDto {
  // Convert tone to UPPERCASE for backend
  const tone = config.tone.toUpperCase()

  // Convert model: 'gpt-4o' -> 'GPT_4O', 'gpt-4o-mini' -> 'GPT_4O_MINI'
  const model = config.model.toUpperCase().replaceAll('-', '_')

  // Convert language to UPPERCASE (auto -> AUTO, pt-BR -> PT, etc)
  const language = config.language.split('-')[0].toUpperCase()

  // Convert audioVoice to UPPERCASE
  const audioVoice = config.audioVoice?.toUpperCase()

  // Handle company website - only include if not empty
  const companyWebsite =
    config.companyWebsite && config.companyWebsite.trim() !== ''
      ? config.companyWebsite
      : undefined

  return {
    name: config.name,
    systemPrompt: config.systemPrompt,
    initialMessage: config.initialMessage,
    tone,
    model,
    language,
    timezone: config.timezone,
    responseDelay: config.responseDelay,
    stages: config.stages,
    companyProfile: {
      name: config.companyName,
      sector: config.companySector,
      website: companyWebsite,
      about: config.companyDescription,
    },
    knowledgeBase: {
      files: config.files,
      websites: config.websites,
      youtubeLinks: config.youtubeLinks,
      qaPairs: config.qaPairs,
    },
    isActive: config.isActive,
    canRespondAudio: config.canRespondAudio,
    audioVoice,
  }
}

/**
 * Convert frontend AgentConfig to backend UpdateAgentDto (same as Create for now)
 */
export function toUpdateAgentDto(
  config: Partial<AgentConfig>
): Partial<CreateAgentDto> {
  const dto: Partial<CreateAgentDto> = {}

  if (config.name !== undefined) dto.name = config.name
  if (config.systemPrompt !== undefined) dto.systemPrompt = config.systemPrompt
  if (config.initialMessage !== undefined)
    dto.initialMessage = config.initialMessage
  if (config.tone !== undefined) dto.tone = config.tone.toUpperCase()
  if (config.model !== undefined)
    dto.model = config.model.toUpperCase().replaceAll('-', '_')
  if (config.language !== undefined)
    dto.language = config.language.split('-')[0].toUpperCase()
  if (config.timezone !== undefined) dto.timezone = config.timezone
  if (config.responseDelay !== undefined)
    dto.responseDelay = config.responseDelay
  if (config.stages !== undefined) dto.stages = config.stages
  if (config.isActive !== undefined) dto.isActive = config.isActive
  if (config.canRespondAudio !== undefined)
    dto.canRespondAudio = config.canRespondAudio
  if (config.audioVoice !== undefined)
    dto.audioVoice = config.audioVoice.toUpperCase()

  // Handle company profile
  if (
    config.companyName !== undefined ||
    config.companySector !== undefined ||
    config.companyWebsite !== undefined ||
    config.companyDescription !== undefined
  ) {
    // Handle company website - only include if not empty
    const companyWebsite =
      config.companyWebsite && config.companyWebsite.trim() !== ''
        ? config.companyWebsite
        : undefined

    dto.companyProfile = {
      name: config.companyName,
      sector: config.companySector,
      website: companyWebsite,
      about: config.companyDescription,
    }
  }

  // Handle knowledge base
  if (
    config.files !== undefined ||
    config.websites !== undefined ||
    config.youtubeLinks !== undefined ||
    config.qaPairs !== undefined
  ) {
    dto.knowledgeBase = {
      files: config.files,
      websites: config.websites,
      youtubeLinks: config.youtubeLinks,
      qaPairs: config.qaPairs,
    }
  }

  return dto
}

/**
 * Convert backend AgentEntity to frontend AgentConfig
 */
export function fromAgentEntity(
  entity: AgentEntity
): AgentConfig & { id: number } {
  // Map enums from UPPERCASE to lowercase
  const tone = entity.tone?.toLowerCase() as AgentConfig['tone']
  const model = entity.model
    ?.toLowerCase()
    .replaceAll('_', '-') as AgentConfig['model']
  const language = entity.language?.toLowerCase() || 'auto'
  const audioVoice =
    entity.audioVoice?.toLowerCase() as AgentConfig['audioVoice']

  // Convert status from ACTIVE/INACTIVE to boolean
  const isActive = entity.status === 'ACTIVE'

  // Extract knowledge base
  const kb = (entity.knowledgeBase as any) || {}

  // Extract company profile
  const companyProfile = (entity.companyProfile as any) || {}

  return {
    id: entity.id,
    systemPrompt: entity.systemPrompt,
    name: entity.name,
    initialMessage: entity.initialMessage,
    tone: tone || 'practical',
    model: model || 'gpt-4o-mini',
    language,
    timezone: entity.timezone,
    responseDelay: entity.responseDelay,
    companyName: companyProfile.name || '',
    companySector: companyProfile.sector || '',
    companyWebsite: companyProfile.website || '',
    companyDescription: companyProfile.about || '',
    files: kb.files || [],
    websites: kb.websites || [],
    youtubeLinks: kb.youtube || kb.youtubeLinks || [],
    qaPairs: kb.qaPairs || [],
    isActive,
    canRespondAudio: entity.audioEnabled,
    audioVoice: audioVoice || 'female',
    stages: entity.stages || [],
  }
}

export type Agent = ReturnType<typeof toAgentListItem>

/**
 * Convert backend AgentEntity to simple Agent for listing
 */
export function toAgentListItem(entity: AgentEntity) {
  return {
    id: entity.id,
    name: entity.name,
    model: entity.model?.toLowerCase().replaceAll('_', '-') as
      | 'gpt-4o'
      | 'gpt-4o-mini',
    status:
      entity.status === 'ACTIVE' ? ('active' as const) : ('inactive' as const),
    language: entity.language?.toLowerCase() || 'auto',
    createdAt: entity.createdAt,
    messageCount: entity.messageCount,
    isDefault: entity.isDefault,
  }
}
